"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import placeholder from "../Event/assests/placeholder.png";

import styles from "./banner.module.css";
import Container from "../container";
import CMSLink from "../common/CMSLink";
import CMSTextBlock from "../common/CMSTextBlock";
import Icon from "@/lib/icon";
import { getSEO } from "@/lib/seoHelper";
const PAGE_HEADER_TYPES = new Set(["category", "city", "blog"]);

const getLineText = (line) => {
  if (typeof line === "string") return line.trim();
  if (line && typeof line === "object") {
    return String(line.text || line.label || line.title || "").trim();
  }
  return "";
};

const FallbackImage = ({ src, fallbackSrc, blurDataURL, ...props }) => {
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
  }, [src]);

  return (
    <Image
      src={error || !src ? fallbackSrc : src}
      onError={() => setError(true)}
      blurDataURL={error || !src ? undefined : blurDataURL}
      {...props}
    />
  );
};

const Banner = ({ data, for_home, pageType, breadcrumb }) => {
  if (!data) return null;

  const {
    title,
    image,
    color1,
    color2,
    heading,
    subheading,
    cta,
    banner_image,
    mobile_banner,
    icons,
    headingLines: cmsHeadingLines,
    subheadingLines: cmsSubheadingLines,
    desktop_illustration_banner,
    mobile_illustration_banner,
    textLayout,
    background_color,
  } = data;

  const structuredHeading = (cmsHeadingLines || [])
    .map(getLineText)
    .filter(Boolean)
    .join(" ");
  const structuredSubheading = (cmsSubheadingLines || [])
    .map(getLineText)
    .filter(Boolean)
    .join(" ");
  const hasStructuredText =
    (Array.isArray(cmsHeadingLines) && cmsHeadingLines.length > 0) ||
    (Array.isArray(cmsSubheadingLines) && cmsSubheadingLines.length > 0);

  const displayTitle = structuredHeading || heading || title;
  const displaySubheading = structuredSubheading || subheading;
  const desktopImage = banner_image || image;
  const desktopIllustrationImage = desktop_illustration_banner || image;
  const mobileIllustrationImage = mobile_illustration_banner || image;
  const mobileImage =
    Array.isArray(mobile_banner) && mobile_banner.length > 0
      ? mobile_banner
      : desktopImage;
  const hasCmsImage = Array.isArray(desktopImage) && desktopImage.length > 0;
  const hasIllustration =
    Array.isArray(desktopIllustrationImage) &&
    desktopIllustrationImage.length > 0;

  const resolveColorRgb = (colorValue) => {
    if (!colorValue) return null;
    if (typeof colorValue === "string") {
      const hex = colorValue.trim().replace(/^#/, "");
      const full =
        hex.length === 3
          ? hex
              .split("")
              .map((c) => c + c)
              .join("")
          : hex;
      if (!/^[0-9a-f]{6}$/i.test(full)) return null;
      const num = parseInt(full, 16);
      return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
    }
    if (colorValue?.metaColor?.isValid) {
      const { r, g, b } = colorValue.metaColor;
      return { r, g, b };
    }
    return null;
  };

  const hasBackgroundColor = !!resolveColorRgb(background_color);
  if (
    !displayTitle &&
    !displaySubheading &&
    !hasCmsImage &&
    !hasIllustration &&
    !hasBackgroundColor &&
    !breadcrumb
  ) {
    return null;
  }
  const altText =
    desktopImage?.[0]?.alt ||
    getSEO("common", { categoryName: displayTitle }).banner?.defaultTitle;

  const generateGradient = (colorValue) => {
    const rgb = resolveColorRgb(colorValue);
    if (!rgb) {
      return `
    linear-gradient(
      to top,
      #0215264D 0%,
      #0215263D 10%,
      #0215262E 22%,
      #0215261F 38%,
      #02152614 56%,
      #0215260F 74%,
      #0215260A 90%,
      #FFFFFF 100%
    )
  `;
    }
    const { r, g, b } = rgb;
    return `
    linear-gradient(
      to top,
      rgba(${r}, ${g}, ${b}, 0.08) 0%,
      rgba(${r}, ${g}, ${b}, 0.07) 10%,
      rgba(${r}, ${g}, ${b}, 0.07) 20%,
      rgba(${r}, ${g}, ${b}, 0.06) 30%,
      rgba(${r}, ${g}, ${b}, 0.04) 40%,
      rgba(${r}, ${g}, ${b}, 0.04) 50%,
      rgba(${r}, ${g}, ${b}, 0.02) 60%,
      rgba(${r}, ${g}, ${b}, 0.02) 70%,
      rgba(${r}, ${g}, ${b}, 0.02) 80%,
      rgba(${r}, ${g}, ${b}, 0.01) 90%,
      #fff 100%
    )
   
  `;
  };

  //       linear-gradient(
  //   to top,
  //   rgba(${r}, ${g}, ${b}, 0.25) 0%,
  //   rgba(${r}, ${g}, ${b}, 0.20) 10%,
  //   rgba(${r}, ${g}, ${b}, 0.18) 22%,
  //   rgba(${r}, ${g}, ${b}, 0.12) 38%,
  //   rgba(${r}, ${g}, ${b}, 0.08) 56%,
  //   rgba(${r}, ${g}, ${b}, 0.06) 74%,
  //   rgba(${r}, ${g}, ${b}, 0.04) 90%,
  //   #fff 100%
  // )
  const getCircleColor = (colorValue) => {
    const rgb = resolveColorRgb(colorValue);
    if (!rgb) return "rgba(234, 132, 106, 0.30)";
    const { r, g, b } = rgb;
    return `rgba(${r}, ${g}, ${b}, 0.80)`;
  };

  const headingLines = (heading || title || "")
    .split(",")
    .map((line) => line.trim())
    .filter(Boolean);

  const subheadingWords = (subheading || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const subheadingLead = subheadingWords.slice(0, -1).join(" ");
  const subheadingAccent = subheadingWords.slice(-1)[0];

  const featureIcons = Array.isArray(icons) ? icons.filter(Boolean) : [];

  const isHomeBanner = for_home || pageType === "home";

  const isPageHeaderBanner =
    PAGE_HEADER_TYPES.has(pageType) || (!isHomeBanner && !hasCmsImage);
  const bgImageDullClass = pageType === "city" ? styles.bgImageDull : "";
  const isCutoutBanner = false;

  const useTextOnlyStyle = !hasCmsImage || isCutoutBanner;

  const useStructuredTextBlock = hasStructuredText;

  const variantClass = isHomeBanner
    ? styles.bannerHome
    : isCutoutBanner
      ? styles.bannerCutout
      : isPageHeaderBanner
        ? styles.bannerPageHeader
        : "";
  const structuredAccentColor =
    cmsHeadingLines?.[0]?.color ||
    cmsSubheadingLines?.[0]?.color ||
    color1 ||
    "#323232";
  const bannerStyle =
    hasCmsImage && !isCutoutBanner
      ? useStructuredTextBlock
        ? { "--banner-accent": structuredAccentColor }
        : undefined
      : {
          background: generateGradient(background_color),
          ...(useStructuredTextBlock ? {} : {}),
        };

  return (
    <section className={styles.section}>
      <div className={`${styles.banner} ${variantClass}`} style={bannerStyle}>
        <Container className={"h-full"}>
          {/* Illustrations are above the fold and, without a background
              image (C/D), the LCP element: next/image lazy-loads by default,
              which held city-hub LCP back ~3.8s on mobile. The hidden
              breakpoint's copy has `sizes` 0px, so it costs one tiny file. */}
          {/* Scenario A: Background Image + Illustration */}
          {hasCmsImage && hasIllustration && !isCutoutBanner && (
            <>
              <FallbackImage
                priority
                src={desktopImage?.[0]?.url}
                fallbackSrc={placeholder}
                placeholder="blur"
                blurDataURL={desktopImage?.[0]?.thumbnail}
                alt={altText}
                fill
                sizes="(max-width: 767px) 0px, 100vw"
                className={`${styles.bgImage} ${styles.desktopOnly} ${bgImageDullClass}`}
              />
              <div
                className={`${styles.illustrationBox} ${styles.desktopOnly} ${isHomeBanner ? styles.illustrationHome : styles.illustrationPage}`}
              >
                <Image
                  src={desktopIllustrationImage?.[0]?.url}
                  placeholder="blur"
                  blurDataURL={desktopIllustrationImage?.[0]?.thumbnail}
                  alt={altText}
                  fill
                  sizes="(max-width: 767px) 0px, 55vw"
                  className={styles.bgIllustrationImage}
                  loading="eager"
                />
              </div>
              <FallbackImage
                priority
                src={mobileImage?.[0]?.url}
                fallbackSrc={placeholder}
                placeholder="blur"
                blurDataURL={mobileImage?.[0]?.thumbnail}
                alt={altText}
                fill
                sizes="(max-width: 767px) 100vw, 0px"
                className={`${styles.bgImage} ${styles.mobileOnly} ${bgImageDullClass}`}
              />
              <div
                className={`${styles.illustrationBox} ${styles.mobileOnly} ${isHomeBanner ? styles.illustrationHome : styles.illustrationPage}`}
              >
                <Image
                  src={mobileIllustrationImage?.[0]?.url}
                  placeholder="blur"
                  blurDataURL={mobileIllustrationImage?.[0]?.thumbnail}
                  alt={altText}
                  fill
                  sizes="(max-width: 767px) 100vw, 0px"
                  className={styles.bgIllustrationImage}
                  loading="eager"
                />
              </div>
            </>
          )}

          {/* Scenario B: Background Image Only (No Illustration) */}
          {hasCmsImage && !hasIllustration && !isCutoutBanner && (
            <>
              <FallbackImage
                priority
                src={desktopImage?.[0]?.url}
                fallbackSrc={placeholder}
                placeholder="blur"
                blurDataURL={desktopImage?.[0]?.thumbnail}
                alt={altText}
                fill
                sizes="(max-width: 767px) 0px, 100vw"
                className={`${styles.bgImage} ${styles.desktopOnly} ${bgImageDullClass}`}
              />
              <FallbackImage
                priority
                src={mobileImage?.[0]?.url}
                fallbackSrc={placeholder}
                placeholder="blur"
                blurDataURL={mobileImage?.[0]?.thumbnail}
                alt={altText}
                fill
                sizes="(max-width: 767px) 100vw, 0px"
                className={`${styles.bgImage} ${styles.mobileOnly} ${bgImageDullClass}`}
              />
            </>
          )}
          {/* Scenario C: Background Color + Optional Illustration */}
          {!hasCmsImage && hasBackgroundColor && (
            <>
              <div
                className={styles.bgColorGradient}
                style={{ background: generateGradient(background_color) }}
              />
              <div className={styles.circleWrap}>
                {/* <span
                  className={styles.circle}
                  style={{ background: getCircleColor(background_color) }}
                /> */}
              </div>
            </>
          )}

          {/* Scenario C + Illustration: Color Gradient + Illustration */}
          {!hasCmsImage && hasBackgroundColor && hasIllustration && (
            <>
              <div
                className={`${styles.illustrationBox} ${styles.desktopOnly} ${isHomeBanner ? styles.illustrationHome : styles.illustrationPage}`}
              >
                <Image
                  src={desktopIllustrationImage?.[0]?.url}
                  placeholder="blur"
                  blurDataURL={desktopIllustrationImage?.[0]?.thumbnail}
                  alt={altText}
                  fill
                  sizes="(max-width: 767px) 0px, 55vw"
                  className={styles.bgIllustrationImage}
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
              <div
                className={`${styles.illustrationBox} ${styles.mobileOnly} ${isHomeBanner ? styles.illustrationHome : styles.illustrationPage}`}
              >
                <Image
                  src={mobileIllustrationImage?.[0]?.url}
                  placeholder="blur"
                  blurDataURL={mobileIllustrationImage?.[0]?.thumbnail}
                  alt={altText}
                  fill
                  sizes="(max-width: 767px) 100vw, 0px"
                  className={styles.bgIllustrationImage}
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            </>
          )}

          {/* Scenario D: Illustration Only (No Background Image or Color) */}
          {!hasCmsImage && !hasBackgroundColor && hasIllustration && (
            <>
              <div
                className={`${styles.illustrationBox} ${styles.desktopOnly} ${isHomeBanner ? styles.illustrationHome : styles.illustrationPage}`}
              >
                <Image
                  src={desktopIllustrationImage?.[0]?.url}
                  placeholder="blur"
                  blurDataURL={desktopIllustrationImage?.[0]?.thumbnail}
                  alt={altText}
                  fill
                  sizes="(max-width: 767px) 0px, 55vw"
                  className={styles.bgIllustrationImage}
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
              <div
                className={`${styles.illustrationBox} ${styles.mobileOnly} ${isHomeBanner ? styles.illustrationHome : styles.illustrationPage}`}
              >
                <Image
                  src={mobileIllustrationImage?.[0]?.url}
                  placeholder="blur"
                  blurDataURL={mobileIllustrationImage?.[0]?.thumbnail}
                  alt={altText}
                  fill
                  sizes="(max-width: 767px) 100vw, 0px"
                  className={styles.bgIllustrationImage}
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            </>
          )}

          {/* Legacy Cutout Banner (for backward compatibility) */}
          {isCutoutBanner && (
            <div className={styles.circleWrap}>
              <span
                className={styles.circle}
                style={{
                  background: hasBackgroundColor
                    ? getCircleColor(background_color)
                    : color2,
                }}
              />
            </div>
          )}

          {isCutoutBanner && (
            <div className={styles.cutoutImageWrap}>
              <FallbackImage
                priority
                src={desktopImage?.[0]?.url}
                fallbackSrc={placeholder}
                placeholder="blur"
                blurDataURL={desktopImage?.[0]?.thumbnail}
                alt={altText}
                fill
                sizes="(max-width: 767px) 45vw, 320px"
                className={styles.cutoutImage}
              />
            </div>
          )}

          <div
            className={`${styles.content} ${!hasCmsImage ? styles.contentFull : ""} ${useStructuredTextBlock ? (isHomeBanner ? styles.contentHomeStructured : styles.contentPageStructured) : ""}`}
          >
            {breadcrumb && (
              <div className={styles.breadcrumbWrapper}>{breadcrumb}</div>
            )}

            {useStructuredTextBlock ? (
              <CMSTextBlock
                headingLines={cmsHeadingLines}
                subheadingLines={cmsSubheadingLines}
                heading={displayTitle}
                subheading={displaySubheading}
                titleTag="h1"
                textLayout={textLayout}
                maxWidth={textLayout?.maxWidth || "medium"}
                className={
                  isHomeBanner ? styles.textBlockHome : styles.textBlockPage
                }
                headingClassName={
                  isHomeBanner
                    ? styles.headingHomeStructured
                    : styles.headingPageStructured
                }
                subheadingClassName={
                  isHomeBanner
                    ? styles.subheadingHomeStructured
                    : styles.subheadingPageStructured
                }
                defaultHeadingColor={color1 || "#051C5B"}
                defaultSubheadingColor={color1 || "rgba(2, 21, 38, 0.8)"}
              />
            ) : (
              headingLines.length > 0 && (
                <h1
                  style={{ color: useTextOnlyStyle ? color1 : undefined }}
                  className={`capitalize ${!useTextOnlyStyle ? styles.headingOnImage : ""}`}
                >
                  {headingLines.map((line, index) => (
                    <span
                      key={index}
                      className={
                        !useTextOnlyStyle && index === headingLines.length - 1
                          ? styles.headingAccent
                          : undefined
                      }
                    >
                      {line.replace("-", " ")}
                    </span>
                  ))}
                </h1>
              )
            )}

            {/* {hasCmsImage &&
              !isCutoutBanner &&
              (subheading || cta?.label || featureIcons.length > 0) && (
                <div className={styles.divider}>
                  <span className={styles.dividerLine} />
                  <span className={styles.dividerDot} />
                  <span className={styles.dividerLine} />
                </div>
              )} */}

            {!useStructuredTextBlock && subheading && (
              <p
                className={`${styles.subheading} ${hasCmsImage && !isCutoutBanner ? styles.subheadingOnImage : ""}`}
              >
                {hasCmsImage && !isCutoutBanner ? (
                  <>
                    {subheadingLead && `${subheadingLead} `}
                    {subheadingAccent && (
                      <span className={styles.subheadingAccentWord}>
                        {subheadingAccent}
                      </span>
                    )}
                  </>
                ) : (
                  subheading
                )}
              </p>
            )}

            {cta?.label && (
              <div
                className={`${styles.cta} ${useStructuredTextBlock ? styles.ctaHomeStructured : ""}`}
              >
                <CMSLink config={cta} className={styles.ctaButton}>
                  {cta.label}
                </CMSLink>
              </div>
            )}

            {hasCmsImage && !isCutoutBanner && featureIcons.length > 0 && (
              <div
                className={`${styles.iconRow} ${useStructuredTextBlock ? styles.iconRowHomeStructured : ""}`}
              >
                {featureIcons.map((item, index) => {
                  const iconValue = item?.icon || item?.value;
                  const label = item?.label || item?.title || item?.text;
                  if (!iconValue && !label) return null;

                  return (
                    <div
                      className={`${styles.iconItem} ${useStructuredTextBlock ? styles.iconItemHomeStructured : ""}`}
                      key={index}
                    >
                      {iconValue && (
                        <Icon
                          value={iconValue}
                          color={
                            useStructuredTextBlock
                              ? structuredAccentColor
                              : "var(--banner-accent, #d9a94f)"
                          }
                          className={styles.iconGlyph}
                        />
                      )}
                      {label && (
                        <span
                          className={`${styles.iconLabel} ${useStructuredTextBlock ? styles.iconLabelDark : ""}`}
                        >
                          {label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {!hasCmsImage && (
            <div className={styles.circleWrap}>
              <span className={styles.circle} style={{ background: color2 }} />
            </div>
          )}
        </Container>
      </div>
    </section>
  );
};

export default Banner;
